import { FunctionComponent } from 'react';

import { isB2C } from '@/core/settings/utils';
import { merchandisingLabels } from '@/core/tamagoshi/MerchandisingTag/interfaces';
import {
  Label,
  LabelVariant,
} from '@/core/tamagoshiTailwind/components/Label/Label';
import { TrendDownIcon } from '@/core/tamagoshiTailwind/icons/TrendDownIcon';
import { PromoTags } from '@/productDiscovery/CommercialAnimation/interfaces/tags';
import { getTags } from '@/productDiscovery/CommercialAnimation/utils/getProductPromoTags';
import {
  blackFridayLabel,
  loweredPriceLabel,
  manomanoDaysLabel,
  proDaysLabel,
  salesLabel,
} from '@/productDiscovery/productCards/translations/translations';
import { promoTag } from '@/productDiscovery/ProductPage/b2c/components/AddToCartBlock/components/PromoBlock/translations';
import { RhinoProductPrice } from '@/productDiscovery/ProductPage/interfaces/RhinoProduct';

interface Props {
  price?:
    | {
        currentPrice: {
          amountWithVat: number;
          amountWithoutVat: number;
          currency: string;
        };
        retailPrice: {
          amountWithVat: number;
          amountWithoutVat: number;
          currency: string | undefined;
        };
      }
    | RhinoProductPrice;
  tags: PromoTags[];
}

export const getPromoLabelData = (
  tags: PromoTags[],
  price?:
    | {
        currentPrice: {
          amountWithVat: number;
          amountWithoutVat: number;
          currency: string;
        };
        retailPrice: {
          amountWithVat: number;
          amountWithoutVat: number;
          currency: string | undefined;
        };
      }
    | RhinoProductPrice,
) => {
  const promoTags = getTags(
    price?.currentPrice?.amountWithVat ?? 0,
    price?.retailPrice?.amountWithVat ?? 0,
    tags,
  );
  if (promoTags.length === 0) return undefined;

  const withPromo = promoTags.includes(merchandisingLabels.PROMO);
  const withBlackFriday = promoTags.includes(merchandisingLabels.BLACKFRIDAY);
  const withSales = promoTags.includes(merchandisingLabels.SALES);
  const withManoManoDays = promoTags.includes(merchandisingLabels.MANOMANODAYS);
  const withLoweredPrice = promoTags.includes(
    merchandisingLabels.LOWERED_PRICE,
  );

  let promoType = undefined;
  if (withPromo) promoType = merchandisingLabels.PROMO;
  if (withBlackFriday) promoType = merchandisingLabels.BLACKFRIDAY;
  if (withSales) promoType = merchandisingLabels.SALES;
  if (withManoManoDays) promoType = merchandisingLabels.MANOMANODAYS;
  if (withLoweredPrice) promoType = merchandisingLabels.LOWERED_PRICE;

  let labelVariant: LabelVariant = 'PromotionPrimary';
  let labelText = '';
  if (promoType === merchandisingLabels.PROMO) {
    labelText = promoTag;
  }
  if (promoType === merchandisingLabels.BLACKFRIDAY) {
    labelVariant = 'FoundationQuinary';
    labelText = blackFridayLabel;
  }
  if (promoType === merchandisingLabels.SALES) {
    labelText = salesLabel;
  }
  if (promoType === merchandisingLabels.MANOMANODAYS) {
    labelText = isB2C() ? manomanoDaysLabel : proDaysLabel;
  }
  if (promoType === merchandisingLabels.LOWERED_PRICE) {
    labelText = loweredPriceLabel;
  }

  return {
    labelText,
    labelVariant,
    promoType,
  };
};

export const PromoLabel: FunctionComponent<Props> = ({ price, tags }) => {
  const labelData = getPromoLabelData(tags, price);

  return labelData ? (
    <Label
      text={labelData.labelText}
      variant={labelData.labelVariant}
      icon={
        labelData.promoType === merchandisingLabels.LOWERED_PRICE
          ? TrendDownIcon
          : undefined
      }
    />
  ) : null;
};
